<template>
    <message-hub-sidebar
        :items="scheduledMessages"
        :loading="loading"
        :error="Boolean(error)"
        :emptyMessage="fetchParams.search ? 'No scheduled messages found matching your search.' : 'No results found'"
        @retry="fetchFirstPage()"
        @scroll="handleScroll"
    >
        <template v-slot:filters>
            <vx-input
                ref="searchEl"
                v-model="fetchParams.search"
                size="md"
                placeholder="Search"
                maxlength="191"
                data-test="search-input"
            >
                <template v-slot:right>
                    <div class="flex items-center gap-2">
                        <span class="flex items-center text-sm gap-0.5 text-slate-300">
                            <template v-if="usesCommandKey">
                                <font-awesome-icon widthAuto :icon="faCommand"></font-awesome-icon>
                            </template>
                            <template v-else> Ctrl </template>
                            <font-awesome-icon widthAuto :icon="faSquareK"></font-awesome-icon>
                        </span>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </div>
                </template>
            </vx-input>
        </template>

        <!-- Filter Items -->
        <template v-slot:filterItems>
            <message-hub-sidebar-sort-item
                @click="fetchParams.sort_dir = 'desc'"
                :active="fetchParams.sort_dir === 'desc'"
                :icon="faArrowUp"
                data-test="oldest-first"
            >
                Oldest First
            </message-hub-sidebar-sort-item>

            <message-hub-sidebar-sort-item
                @click="fetchParams.sort_dir = 'asc'"
                :active="fetchParams.sort_dir === 'asc'"
                :icon="faArrowDown"
                data-test="newest-first"
            >
                Newest First
            </message-hub-sidebar-sort-item>
        </template>

        <!-- Action Buttons -->
        <template v-slot:actionButtons>
            <router-button
                :to="{ name: 'message-hub.new-message' }"
                size="md"
                color="primary"
                class="md:hidden mdShortHeight:flex"
                data-test="new-message"
                aria-label="New message"
            >
                <font-awesome-icon widthAuto :icon="faMessagePlus"></font-awesome-icon>
            </router-button>
        </template>

        <!-- Custom Alerts -->
        <template v-slot:alerts>
            <vx-alert v-if="fetchParams.search" :closeable="false" color="info">
                <div class="flex items-center justify-between">
                    <div class="grow">Filters are applied.</div>
                    <vx-button @click="clearFilters()" color="info" size="sm" data-test="clear-filters">
                        Clear
                        <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                    </vx-button>
                </div>
            </vx-alert>
        </template>

        <template v-slot:mobileHeader>
            <h1 class="text-2xl font-extrabold mb-0">Scheduled Messages</h1>
        </template>

        <template v-slot:content="{ items }">
            <message-hub-scheduled-message-sidebar-row
                v-for="item in items"
                :key="item.id"
                :message="item"
            ></message-hub-scheduled-message-sidebar-row>
        </template>
    </message-hub-sidebar>
</template>

<script setup>
import {
    faArrowDown,
    faArrowUp,
    faCommand,
    faMagnifyingGlass,
    faMessagePlus,
    faSquareK,
    faUndo,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxInput } from '@voxie/frontend-components';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import MessageHubSidebarSortItem from '~/components/message-hub/message-hub-sidebar/MessageHubSidebarSortItem.vue';
import debounce from '~/utils/debounce';
import MessageHubSidebar from '~/components/message-hub/message-hub-sidebar/MessageHubSidebar.vue';
import { useScheduledMessages } from '~/components/message-hub/useScheduledMessages';
import MessageHubScheduledMessageSidebarRow from './MessageHubScheduledMessageSidebarRow.vue';
import { useRouter } from 'vue-router';
import RouterButton from '~/components/general/RouterButton.vue';
import { useEventListener } from '@vueuse/core';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { isAuthErrorEcho } from '~/utils/helpers';

const router = useRouter();

const { getContext } = useCurrentGroup();

const usesCommandKey = /iPad|iPhone|Mac OS/.test(navigator.userAgent);

const { scheduledMessages, fetchFirstPage, fetchData, loading, fetchParams, resetFilters, error } =
    useScheduledMessages();

const initialSetupComplete = ref(false);

const channelName = computed(() => {
    const { teamId, orgUnitId, groupId } = getContext();
    if (!orgUnitId && !groupId) {
        return `scheduled_messages.${teamId}`;
    }
    if (orgUnitId) {
        return `scheduled_messages.${teamId}.org_unit.${orgUnitId}`;
    }
    return `scheduled_messages.${teamId}.${groupId}`;
});

watch(
    () => fetchParams.value.search,
    debounce(() => {
        if (initialSetupComplete.value) {
            fetchFirstPage(getContext());
        }
    }, 300)
);

watch(
    () => fetchParams.value.sort_dir,
    debounce(() => {
        if (initialSetupComplete.value) {
            fetchFirstPage(getContext());
        }
    }, 150)
);

/**
 * @param {Event} event
 */
const handleScroll = (event) => {
    if (!event.target) {
        return;
    }

    const el = event.target;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const height = el.offsetHeight;
    const threshold = window.innerHeight * 0.5;
    if (scrollTop + height >= scrollHeight - threshold && !loading.value && fetchParams.value.cursor) {
        fetchData(getContext());
    }
};

const clearFilters = () => {
    resetFilters();

    router.replace({
        name: 'message-hub.scheduled-messages',
    });
};

const sortMessages = (messages) => {
    return messages.sort((a, b) => {
        if (fetchParams.value.sort_dir === 'asc') {
            return new Date(a.send_at) - new Date(b.send_at);
        }

        return new Date(b.send_at) - new Date(a.send_at);
    });
};

const searchEl = ref();
useEventListener(window, 'keydown', (event) => {
    if ((event.metaKey && event.key === 'k') || (event.ctrlKey && event.key === 'k')) {
        event.preventDefault();
        searchEl.value?.field?.focus?.();
    }
});

const pendingInsertions = new Set();

const insertScheduledMessage = (message) => {
    if (scheduledMessages.value.some((m) => m.id === message.id)) {
        return;
    }

    scheduledMessages.value = sortMessages([...scheduledMessages.value, message]);
};

const cancelPendingInsertions = () => {
    pendingInsertions.forEach((insertion) => clearTimeout(insertion));
    pendingInsertions.clear();
};

const handleScheduledMessageCreated = ({ contact: payloadContact, scheduled_message, user }) => {
    // Delay to ensure server state is updated before adding the new scheduled message.
    // This avoids race conditions where the client receives the event before the server processes it.
    // TODO: Replace this workaround when an update endpoint for scheduled messages is available.
    const insertion = setTimeout(() => {
        pendingInsertions.delete(insertion);
        insertScheduledMessage({ ...scheduled_message, contact: payloadContact, user });
    }, 1000);

    pendingInsertions.add(insertion);
};

const removeScheduledMessage = ({ scheduled_message }) => {
    scheduledMessages.value = scheduledMessages.value.filter((m) => m.id !== scheduled_message.id);
};

const handleScheduledMessageCancelled = ({ data }) => {
    removeScheduledMessage(data);
};

let echoChannel;

onMounted(() => {
    resetFilters();
    fetchFirstPage(getContext()).then(() => (initialSetupComplete.value = true));

    const echoErrors = [];

    echoChannel = Echo.private(channelName.value);
    echoChannel
        .listen('ScheduledMessageCreated', handleScheduledMessageCreated)
        .listen('ScheduledMessageEnqueued', removeScheduledMessage)
        .listen('ScheduledMessageCancelled', handleScheduledMessageCancelled)
        .error?.((e) => {
            if (echoErrors.length <= 3 && !isAuthErrorEcho(e)) {
                window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
                echoErrors.push(e);
            }
        });
});

onUnmounted(() => {
    cancelPendingInsertions();

    echoChannel
        ?.stopListening('ScheduledMessageCreated', handleScheduledMessageCreated)
        .stopListening('ScheduledMessageEnqueued', removeScheduledMessage)
        .stopListening('ScheduledMessageCancelled', handleScheduledMessageCancelled);
});
</script>
